import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { Home, CreditCard, Tag, BarChart3, LogOut, FileText, Settings, Landmark } from 'lucide-react';

import TransactionPage from './components/TransactionPage';
import Analytics from './components/Analytics';
import Dashboard from './components/Dashboard';
import References from './components/References';
import LoginPage from './components/LoginPage';
import ReportsPage from './components/ReportsPage';
import SettingsPage from './components/SettingsPage';
import LoansPage from './components/LoansPage';
import { DialogProvider } from './components/ui/DialogProvider';

import { BalanceProvider } from './context/BalanceContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';

import '@fontsource/manrope';
import '@fontsource/inter';

const NavigationRail = () => {
  const location = useLocation();

  const items = [
    { to: '/', label: 'Главная', icon: Home },
    { to: '/transactions', label: 'Операции', icon: CreditCard },
    { to: '/references', label: 'Справочники', icon: Tag },
    { to: '/analytics', label: 'Аналитика', icon: BarChart3 },
    { to: '/reports', label: 'Отчеты', icon: FileText },
    { to: '/loans', label: 'Кредиты', icon: Landmark },
    { to: '/settings', label: 'Настройки', icon: Settings },
  ];

  return (
    <aside className="sticky top-0 z-40 w-full border-b border-line bg-glass px-4 py-3 backdrop-blur-lg shadow-glass lg:fixed lg:left-0 lg:top-0 lg:h-full lg:w-40 lg:border-b-0 lg:border-r lg:px-3 lg:py-8">
      <div className="flex w-full items-center justify-between gap-4 lg:h-full lg:flex-col">
        <div className="flex w-full items-center gap-4 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:flex-col lg:items-center lg:gap-6 lg:overflow-visible">
          {items.map((item) => {
            const isActive = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex flex-none items-center gap-2 text-sm lg:flex-col lg:items-center lg:gap-1.5 lg:w-full lg:px-3 lg:py-2 rounded-xl transition-colors ${
                  isActive ? 'text-accent bg-accent/10' : 'text-ink-subtle'
                }`}
              >
                <Icon size={18} />
                <span className="text-center leading-tight">{item.label}</span>
              </Link>
            );
          })}
        </div>
        <div className="hidden text-[10px] uppercase tracking-[0.28em] text-muted lg:block">2026</div>
      </div>
    </aside>
  );
};

const TopStrip = () => {
  const { isAuthenticated, logout } = useAuth();

  return (
    <div className="flex flex-col gap-6 py-8">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-[0.4em] text-muted">Finance Workspace</p>
          <h1 className="text-3xl font-semibold text-ink sm:text-4xl">BudgetApp</h1>
        </div>
        <div className="flex items-center gap-4 text-sm text-ink-subtle">
          {isAuthenticated && (
            <button
              onClick={logout}
              className="rounded-full border border-line px-4 py-2 text-xs uppercase tracking-[0.28em]"
              type="button"
            >
              <span className="inline-flex items-center gap-2">
                <LogOut size={14} />
                Выход
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const RequireAuth = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
};

function App() {
  const { isAuthenticated } = useAuth();

  return (
    <ThemeProvider>
      <DialogProvider>
      <BalanceProvider>
        <Router>
          <div className="min-h-screen bg-base text-ink">
            {isAuthenticated && <NavigationRail />}
            <div className="mx-auto w-full max-w-[104rem] px-6 pb-20 lg:pl-56">
              {isAuthenticated && <TopStrip />}
              <main className="min-h-[60vh]">
                <Routes>
                  <Route path="/login" element={<LoginPage />} />

                  <Route
                    path="/"
                    element={
                      <RequireAuth>
                        <Dashboard />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/transactions"
                    element={
                      <RequireAuth>
                        <TransactionPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/analytics"
                    element={
                      <RequireAuth>
                        <Analytics />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <RequireAuth>
                        <ReportsPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/references"
                    element={
                      <RequireAuth>
                        <References />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/loans"
                    element={
                      <RequireAuth>
                        <LoansPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <RequireAuth>
                        <SettingsPage />
                      </RequireAuth>
                    }
                  />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
            </div>
          </div>
        </Router>
      </BalanceProvider>
      </DialogProvider>
    </ThemeProvider>
  );
}

const AppWithAuth = () => (
  <AuthProvider>
    <App />
  </AuthProvider>
);

export default AppWithAuth;
